import { AnimatePresence, motion } from "framer-motion";
import { Briefcase, Building, Lock, LockIcon } from "lucide-react";
import React, { useEffect, useState } from "react";

const steps = [
	{
		title: "Email the job",
		description: (
			<>
				Send the job description to{" "}
				<strong className="text-gray-800">promotejobs@dproz.com</strong>
				. PDF, Word, or plain text — whatever you have.
			</>
		),
	},
	{
		title: "We prepare the listing",
		description:
			"We organize the job so candidates clearly understand the role, requirements, and how to apply.",
	},
	{
		title: "We publish and promote",
		description:
			"Your job goes live on our board and gets shared across social media, WhatsApp, and our candidate email lists.",
	},
	{
		title: "You get your link",
		description:
			"A shareable link to your live job post — share it anywhere for candidates to apply.",
	},
];

function EmailMockup() {
	return (
		<div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
			<div className="flex items-center gap-3 px-3.5 py-2.5 bg-gray-100 border-b border-gray-300/60">
				<div className="flex gap-2 shrink-0">
					<div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
					<div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
					<div className="w-3 h-3 rounded-full bg-[#28C840]" />
				</div>
				<div className="flex-1 text-center">
					<span className="text-xs text-gray-600 font-inter">
						New Message
					</span>
				</div>
			</div>
			<div className="p-5 space-y-3 text-sm font-inter">
				<div className="flex gap-2">
					<span className="text-gray-600 w-14 shrink-0">To:</span>
					<span className="text-blue-600">promotejobs@dproz.com</span>
				</div>
				<div className="flex gap-2">
					<span className="text-gray-600 w-14 shrink-0">
						Subject:
					</span>
					<span className="text-gray-800">
						Job Opening: Senior Sales Manager
					</span>
				</div>
				<hr className="border-gray-100" />
				<div className="text-gray-700 space-y-2 text-[13px] leading-relaxed">
					<p>Hello Dproz,</p>
					<p>
						We're looking for a Senior Sales Manager to join our
						team in Dar es Salaam.
					</p>
					<p>
						Location: Dar es Salaam
						<br />
						Experience: 3+ years
						<br />
						Deadline: April 30, 2025
						<br />
						Salary: Competitive
					</p>
					<p>Attached is the job description.</p>
					<p>Thank you.</p>
				</div>
				<div className="flex justify-end pt-2">
					<div className="bg-blue-600 text-white text-xs px-4 py-2 rounded-lg flex items-center gap-2">
						<svg
							className="w-3.5 h-3.5"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={2}
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
							/>
						</svg>
						Send
					</div>
				</div>
			</div>
		</div>
	);
}

function JobPostingMockup() {
	return (
		<div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
			<div className="flex items-center gap-3 px-3.5 py-2.5 bg-gray-100 border-b border-gray-300/60">
				<div className="flex gap-2 shrink-0">
					<div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
					<div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
					<div className="w-3 h-3 rounded-full bg-[#28C840]" />
				</div>
				<div className="flex-1 flex items-center gap-2 bg-white/70 rounded-md px-3 py-1.5">
					<LockIcon className="w-3 h-3 text-gray-600 shrink-0" />
					<span className="flex-1 text-center text-xs text-gray-500 font-inter">
						dproz.com
					</span>
					<svg
						className="w-3.5 h-3.5 text-gray-600 shrink-0"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<polyline points="23 4 23 10 17 10" />
						<path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
					</svg>
				</div>
				<svg
					className="w-4 h-4 text-gray-600 shrink-0"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				>
					<path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
					<polyline points="16 6 12 2 8 6" />
					<line x1="12" y1="2" x2="12" y2="15" />
				</svg>
			</div>
			<div className="p-5 font-inter">
				<div className="flex items-center gap-3 mb-4">
					<div className="w-10 h-10 bg-gray-100 rounded-sm flex items-center justify-center">
						<span className="text-[10px] font-bold text-gray-600">
							<Building className="w-5 h-5" />
						</span>
					</div>
					<div>
						<h4 className="font-semibold text-gray-900 text-base">
							Procurement Officer
						</h4>
						<p className="text-xs text-gray-500">
							Titan Manufacturing Company
						</p>
					</div>
				</div>
				<div className="flex gap-4 text-xs text-gray-500 mb-5">
					<span className="flex items-center gap-1">
						<svg
							className="w-3.5 h-3.5"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={2}
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
							/>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
							/>
						</svg>
						Dar es Salaam
					</span>
					<span className="flex items-center gap-1">
						<svg
							className="w-3.5 h-3.5"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={2}
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
							/>
						</svg>
						Apr 05, 2026
					</span>
					<span className="flex items-center gap-1">
						<svg
							className="w-3.5 h-3.5"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={2}
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
							/>
						</svg>
						Full-Time
					</span>
				</div>
				<div className="space-y-3 text-[13px] text-gray-700">
					<div>
						<h5 className="font-semibold text-gray-900 mb-1">
							About the Role
						</h5>
						<p className="leading-relaxed">
							We are looking for a detail-oriented Procurement
							Officer to manage sourcing, supplier relationships,
							and purchasing processes for our manufacturing
							operations in Dar es Salaam.
						</p>
					</div>
					<div>
						<h5 className="font-semibold text-gray-900 mb-1">
							Responsibilities
						</h5>
						<ul className="space-y-1 list-disc list-inside text-gray-600">
							<li>Manage purchase orders and contracts</li>
							<li>Source and evaluate suppliers and vendors</li>
							<li>Ensure compliance with procurement policies</li>
						</ul>
					</div>
					<div>
						<h5 className="font-semibold text-gray-900 mb-1">
							Requirements
						</h5>
						<ul className="space-y-1 list-disc list-inside text-gray-600">
							<li>Degree in Procurement or Supply Chain</li>
							<li>3+ years' procurement experience</li>
							<li>Strong negotiation & vendor management</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	);
}

function SocialMediaMockup() {
	return (
		<div className="bg-gray-50 rounded-xl border border-gray-200 p-8 flex items-center justify-center min-h-[300px]">
			<div className="relative w-full max-w-sm aspect-square">
				<div className="absolute inset-0 flex items-center justify-center">
					<div className="w-16 h-16 bg-white rounded-2xl shadow-md flex items-center justify-center">
						<img
							src="/images/home/logo.svg"
							alt="Dproz"
							className="w-10 h-auto"
						/>
					</div>
				</div>

				<svg
					className="absolute inset-0 w-full h-full opacity-10"
					viewBox="0 0 300 300"
				>
					<circle
						cx="150"
						cy="150"
						r="80"
						fill="none"
						stroke="#94a3b8"
						strokeWidth="1"
						strokeDasharray="4 4"
					/>
					<circle
						cx="150"
						cy="150"
						r="130"
						fill="none"
						stroke="#94a3b8"
						strokeWidth="1"
						strokeDasharray="4 4"
					/>
				</svg>

				<div className="absolute top-4 left-8 w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center shadow-md">
					<svg
						className="w-6 h-6 text-white"
						fill="currentColor"
						viewBox="0 0 24 24"
					>
						<path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
					</svg>
				</div>

				<div className="absolute top-4 right-8 w-12 h-12 rounded-xl bg-blue-700 flex items-center justify-center shadow-md">
					<svg
						className="w-6 h-6 text-white"
						fill="currentColor"
						viewBox="0 0 24 24"
					>
						<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
					</svg>
				</div>

				<div className="absolute top-1/2 -translate-y-1/2 left-0 w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center shadow-md">
					<svg
						className="w-6 h-6 text-white"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						strokeWidth={2}
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
						/>
					</svg>
				</div>

				<div className="absolute top-1/2 -translate-y-1/2 right-0 w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shadow-md">
					<svg
						className="w-6 h-6 text-white"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						strokeWidth={2}
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
						/>
					</svg>
				</div>

				<div className="absolute bottom-4 left-8 w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center shadow-md">
					<svg
						className="w-6 h-6 text-white"
						fill="currentColor"
						viewBox="0 0 24 24"
					>
						<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
					</svg>
				</div>

				<div className="absolute bottom-4 right-8 w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-md">
					<svg className="w-6 h-6" viewBox="0 0 24 24">
						<path
							d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 010 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"
							fill="#EA4335"
						/>
					</svg>
				</div>
			</div>
		</div>
	);
}

function CompanyPageMockup() {
	const jobs = [
		{
			title: "Frontend Developer",
			location: "Arusha, Tanzania",
			date: "Jan 16, 2026",
		},
		{
			title: "Backend Developer",
			location: "Arusha, Tanzania",
			date: "Jan 16, 2026",
		},
		{
			title: "Procurement Officer",
			location: "Arusha, Tanzania",
			date: "Jan 16, 2026",
		},
		{
			title: "Operations Analyst",
			location: "Arusha, Tanzania",
			date: "Jan 16, 2026",
		},
		{
			title: "Logistics Coordinator",
			location: "Arusha, Tanzania",
			date: "Jan 16, 2026",
		},
	];

	return (
		<div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
			<div className="flex items-center gap-3 px-3.5 py-2.5 bg-gray-100 border-b border-gray-300/60">
				<div className="flex gap-2 shrink-0">
					<div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
					<div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
					<div className="w-3 h-3 rounded-full bg-[#28C840]" />
				</div>
				<div className="flex-1 flex items-center gap-2 bg-white/70 rounded-md px-3 py-1.5">
					<LockIcon className="w-3 h-3 text-gray-600 shrink-0" />
					<span className="flex-1 text-center text-xs text-gray-500 font-inter">
						dproz.com
					</span>
					<svg
						className="w-3.5 h-3.5 text-gray-600 shrink-0"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2.5"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<polyline points="23 4 23 10 17 10" />
						<path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
					</svg>
				</div>
				<svg
					className="w-4 h-4 text-gray-600 shrink-0"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
				>
					<path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
					<polyline points="16 6 12 2 8 6" />
					<line x1="12" y1="2" x2="12" y2="15" />
				</svg>
			</div>
			<div className="p-5 font-inter">
				<div className="flex items-center gap-3 mb-3">
					<div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
						<span className="text-[10px] font-bold text-gray-600">
							TITAN
						</span>
					</div>
					<div>
						<h4 className="font-semibold text-gray-900 text-base">
							Titan Manufacturing Co.
						</h4>
						<p className="text-xs text-gray-500">
							manufacturing &middot; procurement &middot;
							logistics
						</p>
					</div>
				</div>
				<div className="flex gap-4 text-xs text-gray-500 mb-4">
					<span>Manufacturing</span>
					<span>Dar es Salaam</span>
					<span>10,001+ employees</span>
				</div>
				<div className="border-b border-gray-200 mb-4">
					<div className="flex gap-6 text-sm">
						<span className="pb-2 border-b-2 border-blue-600 font-medium text-gray-900">
							Jobs
						</span>
						<span className="pb-2 text-gray-600">About</span>
					</div>
				</div>
				<h5 className="font-semibold text-gray-900 text-sm mb-3">
					Open Positions
				</h5>
				<div className="space-y-0 divide-y divide-gray-100">
					{jobs.map((job) => (
						<div
							key={job.title}
							className="flex items-center justify-between py-2.5"
						>
							<div className="flex items-center gap-2.5">
								<div className="w-7 h-7 bg-gray-100 rounded-sm flex items-center justify-center">
									<span className="text-[7px] font-bold text-gray-500">
										<Briefcase className="w-3.5 h-3.5" />
									</span>
								</div>
								<div>
									<p className="text-sm font-medium text-gray-900">
										{job.title}
									</p>
									<p className="text-[11px] text-gray-600">
										{job.location} &middot; {job.date}
									</p>
								</div>
							</div>
							<div className="flex gap-2">
								<button className="text-xs text-gray-500 border border-gray-200 rounded-md px-3 py-1">
									Save
								</button>
								<button className="text-xs text-white bg-blue-600 rounded-md px-3 py-1">
									Explore
								</button>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

const mockups = [
	EmailMockup,
	JobPostingMockup,
	SocialMediaMockup,
	CompanyPageMockup,
];

const CYCLE_SECONDS = 6;

function HiringWorkflow() {
	const [activeStep, setActiveStep] = useState(0);

	useEffect(() => {
		const timer = setTimeout(() => {
			setActiveStep((prev) => (prev + 1) % steps.length);
		}, CYCLE_SECONDS * 1000);
		return () => clearTimeout(timer);
	}, [activeStep]);

	const ActiveMockup = mockups[activeStep];

	return (
		<motion.section
			className="py-14 sm:py-20"
			initial={{ opacity: 0, y: 24 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-64px" }}
			transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
		>
			<div className="max-w-6xl mx-auto px-4">
				<div className="mb-10 sm:mb-14">
					<p className="font-inter text-xs font-semibold text-blue-600 tracking-[0.15em] uppercase mb-4">
						How it works
					</p>
					<h2 className="font-plus-jakarta text-[clamp(1.75rem,4vw,2.75rem)] font-bold text-gray-900 tracking-tight leading-tight max-w-md">
						Four steps.
						<br />
						Zero complexity.
					</h2>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-start">
					{/* Steps */}
					<div className="space-y-1">
						{steps.map((step, index) => {
							const isActive = index === activeStep;
							return (
								<button
									key={index}
									onClick={() => setActiveStep(index)}
									className={`relative w-full text-left flex items-start gap-5 p-5 rounded-xl transition-colors duration-200 cursor-pointer ${
										isActive
											? "bg-gray-50"
											: "hover:bg-gray-50/50"
									}`}
								>
									{/* Progress bar */}
									{isActive && (
										<div className="absolute top-0 left-4 right-4 h-[2px] bg-gray-200/60 rounded-full overflow-hidden">
											<motion.div
												className="h-full bg-blue-600 rounded-full origin-left"
												style={{ width: "100%" }}
												initial={{ scaleX: 0 }}
												animate={{ scaleX: 1 }}
												transition={{
													duration: CYCLE_SECONDS,
													ease: "linear",
												}}
												key={activeStep}
											/>
										</div>
									)}

									{/* Step number */}
									<div
										className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold font-inter transition-colors duration-200 ${
											isActive
												? "bg-blue-600 text-white"
												: "bg-gray-100 text-gray-600"
										}`}
									>
										{index + 1}
									</div>

									{/* Step content */}
									<div className="min-w-0 pt-1">
										<h3
											className={`font-inter font-semibold text-base transition-colors duration-200 ${
												isActive
													? "text-gray-900"
													: "text-gray-600"
											}`}
										>
											{step.title}
										</h3>
										<p
											className={`font-inter text-sm leading-relaxed pt-1.5 transition-colors duration-200 ${
												isActive
													? "text-gray-500"
													: "text-gray-600"
											}`}
										>
											{step.description}
										</p>
									</div>
								</button>
							);
						})}
					</div>

					{/* Mockup */}
					<div className="lg:sticky lg:top-8">
						<AnimatePresence mode="wait">
							<motion.div
								key={activeStep}
								initial={{ opacity: 0, y: 12 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -12 }}
								transition={{
									duration: 0.3,
									ease: [0.22, 1, 0.36, 1],
								}}
							>
								<ActiveMockup />
							</motion.div>
						</AnimatePresence>
					</div>
				</div>
			</div>
		</motion.section>
	);
}

export default HiringWorkflow;
